import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { X, Eye } from 'lucide-react';

const galleryItems = [
  {
    title: 'Goddess Braids',
    category: 'Braiding',
    gradient: 'linear-gradient(135deg, #6B2737 0%, #C9956C 100%)',
    size: 'tall',
    svgAccent: (
      <svg viewBox="0 0 120 200" fill="none" className="w-full h-full opacity-20">
        <path d="M60 10C60 10 30 50 25 100C20 150 40 180 60 190C80 180 100 150 95 100C90 50 60 10 60 10Z"
          stroke="#F5E6D3" strokeWidth="2" fill="none" />
        <path d="M60 10C60 10 50 50 48 100C46 150 52 180 60 190" stroke="#F5E6D3" strokeWidth="1.5" strokeDasharray="4 3" />
        <path d="M45 50C52 55 68 55 75 50" stroke="#F5E6D3" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M38 80C46 86 74 86 82 80" stroke="#F5E6D3" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M35 110C44 116 76 116 85 110" stroke="#F5E6D3" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M38 140C47 146 73 146 82 140" stroke="#F5E6D3" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M45 165C52 170 68 170 75 165" stroke="#F5E6D3" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: 'Bridal Glow Makeup',
    category: 'Makeup',
    gradient: 'linear-gradient(135deg, #C9956C 0%, #F5E6D3 50%, #C9956C 100%)',
    size: 'normal',
    svgAccent: (
      <svg viewBox="0 0 120 120" fill="none" className="w-full h-full opacity-25">
        <circle cx="60" cy="50" r="35" stroke="#6B2737" strokeWidth="2" fill="none" />
        <path d="M48 42C50 38 55 36 60 36C65 36 70 38 72 42" stroke="#6B2737" strokeWidth="2" strokeLinecap="round" />
        <ellipse cx="48" cy="50" rx="5" ry="3" fill="#6B2737" />
        <ellipse cx="72" cy="50" rx="5" ry="3" fill="#6B2737" />
        <path d="M50 62C53 68 67 68 70 62" stroke="#6B2737" strokeWidth="2" strokeLinecap="round" />
        <path d="M38 30C42 24 50 20 60 20C70 20 78 24 82 30" stroke="#6B2737" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 2" />
        <circle cx="60" cy="50" r="3" fill="#6B2737" opacity="0.5" />
      </svg>
    ),
  },
  {
    title: 'Rose Gold Highlights',
    category: 'Coloring',
    gradient: 'linear-gradient(160deg, #8B4A2A 0%, #C9956C 40%, #E8B99A 100%)',
    size: 'normal',
    svgAccent: (
      <svg viewBox="0 0 120 120" fill="none" className="w-full h-full opacity-20">
        <path d="M30 20C30 20 60 15 90 20C100 50 95 90 60 110C25 90 20 50 30 20Z" stroke="#F5E6D3" strokeWidth="2" fill="none" />
        <path d="M40 30C55 28 75 30 85 38" stroke="#F5E6D3" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M35 50C50 48 80 50 88 56" stroke="#F5E6D3" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M38 70C53 68 78 70 85 75" stroke="#F5E6D3" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M42 88C56 86 72 88 78 92" stroke="#F5E6D3" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: 'Spa Day Experience',
    category: 'Spa',
    gradient: 'linear-gradient(135deg, #1A1A1A 0%, #3d1528 100%)',
    size: 'wide',
    svgAccent: (
      <svg viewBox="0 0 240 120" fill="none" className="w-full h-full opacity-15">
        <ellipse cx="120" cy="80" rx="90" ry="30" stroke="#C9956C" strokeWidth="2" fill="none" />
        <path d="M80 80C80 60 100 50 120 50C140 50 160 60 160 80" stroke="#C9956C" strokeWidth="2" strokeLinecap="round" />
        <circle cx="120" cy="40" r="20" fill="none" stroke="#C9956C" strokeWidth="1.5" />
        <path d="M115 35C117 30 123 30 125 35" stroke="#C9956C" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M112 42C116 48 124 48 128 42" stroke="#C9956C" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M50 60C50 60 40 40 50 20C55 12 65 10 70 18" stroke="#C9956C" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 3" opacity="0.5" />
        <path d="M190 60C190 60 200 40 190 20C185 12 175 10 170 18" stroke="#C9956C" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 3" opacity="0.5" />
      </svg>
    ),
  },
  {
    title: 'Box Braids & Beads',
    category: 'Braiding',
    gradient: 'linear-gradient(135deg, #3d1528 0%, #6B2737 60%, #8B4A2A 100%)',
    size: 'normal',
    svgAccent: (
      <svg viewBox="0 0 120 120" fill="none" className="w-full h-full opacity-20">
        {[0, 1, 2, 3, 4].map(i => (
          <g key={i}>
            <line x1={20 + i * 20} y1="10" x2={15 + i * 20} y2="110" stroke="#F5E6D3" strokeWidth="3" strokeLinecap="round" />
            <circle cx={17 + i * 20} cy={40 + i * 5} r="4" fill="#C9956C" />
            <circle cx={16 + i * 20} cy={70 + i * 3} r="3" fill="#F5E6D3" />
          </g>
        ))}
      </svg>
    ),
  },
  {
    title: 'Keratin Treatment',
    category: 'Treatment',
    gradient: 'linear-gradient(160deg, #C9956C 0%, #F5E6D3 50%, #E8B99A 100%)',
    size: 'normal',
    svgAccent: (
      <svg viewBox="0 0 120 120" fill="none" className="w-full h-full opacity-25">
        <path d="M30 20C45 10 75 10 90 20C100 50 95 90 60 110C25 90 20 50 30 20Z"
          stroke="#C9956C" strokeWidth="2" fill="none" />
        {[0,1,2,3,4,5].map(i => (
          <path key={i} d={`M${35 + i*8} 30 C${37+i*8} ${60+i*3} ${38+i*8} ${85+i*2} ${36+i*8} 100`}
            stroke="#C9956C" strokeWidth="1.5" strokeLinecap="round" opacity={0.5 - i * 0.05} />
        ))}
      </svg>
    ),
  },
  {
    title: 'Luxury Manicure',
    category: 'Nails',
    gradient: 'linear-gradient(135deg, #6B2737 0%, #C9956C 50%, #6B2737 100%)',
    size: 'tall',
    svgAccent: (
      <svg viewBox="0 0 120 200" fill="none" className="w-full h-full opacity-20">
        {[0,1,2,3,4].map(i => (
          <g key={i}>
            <path d={`M${15 + i*22} 60 C${13+i*22} 80 ${14+i*22} 120 ${15+i*22} 150`}
              stroke="#F5E6D3" strokeWidth="10" strokeLinecap="round" />
            <ellipse cx={15 + i*22} cy={60} rx="8" ry="12"
              fill="#C9956C" opacity="0.9" />
          </g>
        ))}
        <path d="M10 165C10 165 30 170 60 170C90 170 110 165 110 165"
          stroke="#F5E6D3" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
      </svg>
    ),
  },
  {
    title: 'Natural Twist Out',
    category: 'Natural Hair',
    gradient: 'linear-gradient(135deg, #8B4A2A 0%, #6B2737 50%, #3d1528 100%)',
    size: 'normal',
    svgAccent: (
      <svg viewBox="0 0 120 120" fill="none" className="w-full h-full opacity-20">
        <path d="M60 10C60 10 20 40 20 70C20 95 38 110 60 110C82 110 100 95 100 70C100 40 60 10 60 10Z"
          stroke="#C9956C" strokeWidth="2" fill="none" />
        <path d="M60 10C60 10 55 40 48 60C42 78 45 100 60 110" stroke="#C9956C" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
        <path d="M60 10C60 10 65 40 72 60C78 78 75 100 60 110" stroke="#C9956C" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
        {[0,1,2].map(i => (
          <path key={i} d={`M${30+i*15} ${45+i*10} C${40+i*15} ${55+i*10} ${50+i*15} ${45+i*10} ${60+i*15} ${55+i*10}`}
            stroke="#F5E6D3" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
        ))}
      </svg>
    ),
  },
  {
    title: 'Full Body Massage',
    category: 'Spa',
    gradient: 'linear-gradient(160deg, #1A1A1A 0%, #6B2737 50%, #C9956C 100%)',
    size: 'normal',
    svgAccent: (
      <svg viewBox="0 0 120 120" fill="none" className="w-full h-full opacity-15">
        <ellipse cx="60" cy="60" rx="45" ry="30" fill="none" stroke="#F5E6D3" strokeWidth="2" />
        <circle cx="60" cy="35" r="15" fill="none" stroke="#C9956C" strokeWidth="1.5" />
        <path d="M35 75C35 75 30 90 40 95C50 100 70 100 80 95C90 90 85 75 85 75" stroke="#C9956C" strokeWidth="2" strokeLinecap="round" />
        <path d="M20 60C18 55 20 48 25 48" stroke="#F5E6D3" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M100 60C102 55 100 48 95 48" stroke="#F5E6D3" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

function GalleryCard({ item, index, onClick }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  const heightClass = item.size === 'tall'
    ? 'row-span-2 min-h-[320px]'
    : item.size === 'wide'
    ? 'col-span-2 min-h-[200px]'
    : 'min-h-[220px]';

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.92 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, delay: index * 0.07 }}
      className={`relative rounded-2xl overflow-hidden cursor-pointer group ${heightClass}`}
      style={{ background: item.gradient }}
      onClick={() => onClick(item)}
    >
      {/* SVG art overlay */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        {item.svgAccent}
      </div>

      {/* Grain texture */}
      <div className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
        }}
      />

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-400 flex items-center justify-center">
        <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center">
            <Eye className="w-5 h-5 text-white" />
          </div>
          <span className="font-inter text-white text-xs font-medium tracking-wider uppercase">View</span>
        </div>
      </div>

      {/* Bottom label */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
        <p className="font-inter text-white/60 text-xs tracking-widest uppercase mb-0.5">{item.category}</p>
        <h3 className="font-playfair text-white font-semibold text-sm">{item.title}</h3>
      </div>

      {/* Rose gold corner accent */}
      <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-roseGold opacity-80" />
    </motion.div>
  );
}

export default function Gallery() {
  const [selected, setSelected] = useState(null);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="gallery" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={ref} className="text-center mb-14">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="font-inter text-roseGold text-xs font-semibold tracking-[0.3em] uppercase mb-3"
          >
            Portfolio
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-playfair text-4xl md:text-5xl font-bold text-dark mb-5"
          >
            Our Work
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="w-20 h-0.5 bg-gradient-to-r from-roseGold to-burgundy mx-auto mb-5"
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="font-inter text-charcoal/70 text-base max-w-xl mx-auto"
          >
            Every look tells a story. Browse a selection of our favourite transformations.
          </motion.p>
        </div>

        {/* Masonry grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[180px]">
          {galleryItems.map((item, i) => (
            <GalleryCard key={item.title} item={item} index={i} onClick={setSelected} />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
              className="relative max-w-lg w-full rounded-3xl overflow-hidden shadow-2xl"
              style={{ background: selected.gradient, minHeight: '400px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute inset-0 flex items-center justify-center p-8">
                {selected.svgAccent}
              </div>
              <button
                onClick={() => setSelected(null)}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-white/30 transition-colors z-10"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/70 to-transparent">
                <p className="font-inter text-roseGold text-xs tracking-widest uppercase mb-1">{selected.category}</p>
                <h3 className="font-playfair text-white text-2xl font-bold">{selected.title}</h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
